import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { AuthContext } from '@/context/AuthContext';
import { TokenType } from '@/types';

export const useAuthTokens = () => {
	const { isAuth } = useContext(AuthContext);
	const refreshTimeoutRef = useRef<NodeJS.Timeout>(null);
	const isMountedRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
			if (refreshTimeoutRef.current) {
				clearTimeout(refreshTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!isAuth && refreshTimeoutRef.current) {
			clearTimeout(refreshTimeoutRef.current);
			refreshTimeoutRef.current = null;
		}
	}, [isAuth]);

	const fetchTokens = useCallback(async () => {
		try {
			const refreshToken = localStorage.getItem('refreshToken');
			if (!refreshToken) {
				throw new Error('No refresh token');
			}

			const res = await axios.post(
				`${import.meta.env.VITE_API_URL}api/account/token/refresh/`,
				{ refresh: refreshToken }
			);

			localStorage.setItem('accessToken', res.data.access);
			localStorage.setItem('refreshToken', res.data.refresh);

			refreshTokens();
		} catch (err) {
			if (axios.isAxiosError(err)) {
				console.error(
					'Token refresh failed:',
					err.response?.status,
					err.message
				);
				if (err.response?.status === 401) {
					localStorage.removeItem('accessToken');
					localStorage.removeItem('refreshToken');
					if (isMountedRef.current) {
						globalThis.location.reload();
					}
				}
			} else {
				console.error('Unexpected error:', err);
			}
		}
	}, []);

	const refreshTokens = useCallback(() => {
		if (refreshTimeoutRef.current) {
			clearTimeout(refreshTimeoutRef.current);
			refreshTimeoutRef.current = null;
		}

		const access = localStorage.getItem('accessToken');
		const refresh = localStorage.getItem('refreshToken');

		if (!access || !refresh) {
			return;
		}

		try {
			const decodedAccessToken = jwtDecode<TokenType>(access);
			const expiresIn = decodedAccessToken.exp * 1000 - Date.now();

			if (expiresIn <= 30_000) {
				fetchTokens();
			} else {
				const refreshIn = Math.max(0, expiresIn - 30_000);

				refreshTimeoutRef.current = setTimeout(() => {
					fetchTokens();
				}, refreshIn);
			}
		} catch (error) {
			console.error('Failed to decode token:', error);
			fetchTokens();
		}
	}, [fetchTokens]);

	return {
		refreshTokens,
		fetchTokens,
	};
};
